<template>
  <div class="financial-period-filter">
    <!-- Linha 1: Pessoa Jurídica, Pessoa Física (se habilitado) e Período (Data Início e Fim) lado a lado -->
    <div class="flex flex-wrap gap-2 items-end mb-2">
      <!-- Pessoa Jurídica - só aparece se showInstitutionFilter for true -->
      <div v-if="showLocalInstitutionFilter" class="w-full sm:w-auto flex-grow">
        <label class="text-sm font-medium mb-1 block">Pessoa Jurídica</label>
        <v-select
          v-model="selectedLegalEntity"
          :options="legalEntityOptions"
          :reduce="option => option.id"
          label="name"
          placeholder="Todas"
          @input="handleLegalEntityChange"
          class="w-full"
        >
        </v-select>
      </div>

      <!-- Professor ou Aluno - só aparece se showPeopleFilter for true -->
      <div v-if="showPeopleFilter" class="w-full sm:w-auto flex-grow">
        <label class="text-sm font-medium mb-1 block">{{
          peopleFilterType === 'student' ? 'Aluno' : 'Professor'
        }}</label>
        <v-select
          v-model="selectedPeople"
          :options="peopleOptions"
          :reduce="option => option.id"
          label="name"
          :placeholder="peopleFilterType === 'student' ? 'Todos' : 'Todos'"
          @input="handlePeopleChange"
          class="w-full"
        >
        </v-select>
      </div>

      <!-- Produto - só aparece se showProductFilter for true -->
      <div v-if="showProductFilter" class="w-full sm:w-auto flex-grow">
        <label class="text-sm font-medium mb-1 block">Produto</label>
        <v-select
          v-model="selectedProduct"
          :options="productOptions"
          :reduce="option => option.id"
          label="name"
          placeholder="Todos"
          @input="handleProductChange"
          class="w-full"
        >
        </v-select>
      </div>

      <!-- Data Início -->
      <div class="w-full sm:w-auto">
        <label class="text-sm font-medium mb-1 block">Data Início</label>
        <b-form-input
          v-model="startDate"
          type="date"
          @change="handleDateChange"
          class="w-full"
          style="min-width: 150px;"
        >
        </b-form-input>
      </div>

      <!-- Data Fim -->
      <div class="w-full sm:w-auto">
        <label class="text-sm font-medium mb-1 block">Data Fim</label>
        <b-form-input
          v-model="endDate"
          type="date"
          @change="handleDateChange"
          class="w-full"
          style="min-width: 150px;"
        >
        </b-form-input>
      </div>
    </div>

    <!-- Linha 2: Filtros Rápidos (Botões) e Botão Limpar -->
    <div class="flex flex-wrap items-end justify-between gap-2 mb-1">
      <div class="flex-grow">
        <label class="text-sm font-medium mb-1 block">Período Rápido</label>
        <div class="flex flex-wrap gap-2">
          <b-button
            v-for="period in quickPeriodOptions"
            :key="period.value"
            :variant="
              quickPeriod === period.value ? 'primary' : 'outline-secondary'
            "
            size="sm"
            @click="selectQuickPeriod(period.value)"
          >
            {{ period.text }}
          </b-button>
        </div>
      </div>

      <!-- Botão Limpar Filtros (lado direito) -->
      <div class="w-full flex items-end justify-end ml-auto mt-2 mb-2">
        <b-button variant="outline-danger" size="sm" @click="clearFilters">
          {{ $t('action.clear') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { BFormInput, BButton } from 'bootstrap-vue'
import vSelect from 'vue-select'
import LegalEntityService from '@/services/api/LegalEntityService'
import TeacherService from '@/services/api/TeacherService'
import StudentService from '@/services/api/StudentService'
import ProductService from '@/services/api/ProductService'
import { mapGetters } from 'vuex'

export default {
  name: 'FinancialPeriodFilter',
  components: {
    vSelect,
    BFormInput,
    BButton
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        institution_id: null,
        start_date: null,
        end_date: null,
        teacher_id: null,
        student_id: null,
        product_id: null
      })
    },
    showPeopleFilter: {
      type: Boolean,
      default: false
    },
    showInstitutionFilter: {
      type: Boolean,
      default: true
    },
    peopleFilterType: {
      type: String,
      default: 'teacher', // 'teacher' or 'student'
      validator: value => ['teacher', 'student'].includes(value)
    },
    showProductFilter: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedLegalEntity: null,
      selectedPeople: null,
      selectedProduct: null,
      legalEntityOptions: [],
      peopleOptions: [],
      productOptions: [],
      quickPeriod: 'last_7_days',
      startDate: '',
      endDate: '',
      quickPeriodOptions: [
        { value: 'today', text: 'Hoje' },
        { value: 'yesterday', text: 'Ontem' },
        { value: 'last_7_days', text: 'Últimos 7 dias' },
        { value: 'last_15_days', text: 'Últimos 15 dias' },
        { value: 'last_30_days', text: 'Últimos 30 dias' },
        { value: 'this_month', text: 'Este mês' },
        { value: 'last_month', text: 'Mês anterior' },
        { value: 'custom', text: 'Personalizado' }
      ]
    }
  },
  computed: {
    ...mapGetters('dataAccess', ['activeInstitutionId', 'isSystemAdmin']),
    showLocalInstitutionFilter() {
      if (!this.showInstitutionFilter) {
        return false
      }
      if (this.isSystemAdmin && !this.activeInstitutionId) {
        return true
      }
      return !this.activeInstitutionId
    }
  },
  methods: {
    calculateQuickPeriod(period) {
      const today = new Date()
      let start = new Date()
      let end = new Date()

      switch (period) {
        case 'today':
          start.setHours(0, 0, 0, 0)
          end.setHours(23, 59, 59, 999)
          break
        case 'yesterday':
          start.setDate(today.getDate() - 1)
          start.setHours(0, 0, 0, 0)
          end.setDate(today.getDate() - 1)
          end.setHours(23, 59, 59, 999)
          break
        case 'last_7_days':
          start.setDate(today.getDate() - 7)
          start.setHours(0, 0, 0, 0)
          end.setHours(23, 59, 59, 999)
          break
        case 'last_15_days':
          start.setDate(today.getDate() - 15)
          start.setHours(0, 0, 0, 0)
          end.setHours(23, 59, 59, 999)
          break
        case 'last_30_days':
          start.setDate(today.getDate() - 30)
          start.setHours(0, 0, 0, 0)
          end.setHours(23, 59, 59, 999)
          break
        case 'this_month':
          start = new Date(today.getFullYear(), today.getMonth(), 1)
          end = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
            999
          )
          break
        case 'last_month':
          start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
          start.setHours(0, 0, 0, 0)
          end = new Date(today.getFullYear(), today.getMonth(), 0)
          end.setHours(23, 59, 59, 999)
          break
        default:
          return { start: null, end: null }
      }

      return {
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0]
      }
    },
    selectQuickPeriod(period) {
      this.quickPeriod = period

      if (period === 'custom') {
        return
      }

      const dates = this.calculateQuickPeriod(period)
      this.startDate = dates.start
      this.endDate = dates.end
      this.emitFilters()
    },
    handleDateChange() {
      this.quickPeriod = 'custom'
      this.emitFilters()
    },
    handleLegalEntityChange() {
      this.emitFilters()
    },
    handlePeopleChange() {
      this.emitFilters()
    },
    handleProductChange() {
      this.emitFilters()
    },
    resolveInstitutionId() {
      if (!this.showInstitutionFilter) {
        return null
      }
      if (this.activeInstitutionId) {
        return this.activeInstitutionId
      }
      if (this.showLocalInstitutionFilter) {
        return this.selectedLegalEntity || null
      }
      return null
    },
    emitFilters() {
      const filters = {
        institution_id: this.resolveInstitutionId(),
        start_date: this.startDate || null,
        end_date: this.endDate || null,
        teacher_id:
          this.showPeopleFilter && this.peopleFilterType === 'teacher'
            ? this.selectedPeople || null
            : null,
        student_id:
          this.showPeopleFilter && this.peopleFilterType === 'student'
            ? this.selectedPeople || null
            : null,
        product_id: this.showProductFilter ? this.selectedProduct || null : null
      }
      this.$emit('input', filters)
      this.$emit('change', filters)
    },
    clearFilters() {
      this.selectedLegalEntity = null
      this.selectedPeople = null
      this.selectedProduct = null
      this.quickPeriod = 'last_7_days'
      const dates = this.calculateQuickPeriod('last_7_days')
      this.startDate = dates.start
      this.endDate = dates.end
      this.emitFilters()
    },
    async loadLegalEntities() {
      try {
        const legalEntityService = LegalEntityService.build(this.$vs)
        const response = await legalEntityService.all()
        const entities = Array.isArray(response)
          ? response
          : response.data || []
        this.legalEntityOptions = entities.map(item => ({
          id: item.id,
          name:
            `${item.trading_name || ''} - ${item.company_name || ''}`.trim() ||
            `ID: ${item.id}`
        }))
      } catch (error) {
        console.error('Error loading legal entities:', error)
        this.legalEntityOptions = []
      }
    },
    async loadPeople() {
      if (!this.showPeopleFilter) {
        return
      }

      try {
        let response
        if (this.peopleFilterType === 'student') {
          const studentService = StudentService.build(this.$vs)
          response = await studentService.all()
        } else {
          const teacherService = TeacherService.build(this.$vs)
          response = await teacherService.all()
        }

        // A resposta vem como array direto
        const people = Array.isArray(response) ? response : []
        this.peopleOptions = people.map(item => ({
          id: item.people_id || item.id,
          name: item.name || `ID: ${item.id}`,
          email: item.email || ''
        }))
      } catch (error) {
        console.error('Error loading people:', error)
        this.peopleOptions = []
      }
    },
    async loadProducts() {
      if (!this.showProductFilter) {
        return
      }

      try {
        const productService = ProductService.build(this.$vs)
        const response = await productService.all()

        // A resposta vem como array direto (apenas id e name)
        const products = Array.isArray(response) ? response : []
        this.productOptions = products.map(item => ({
          id: item.id,
          name: item.name || `ID: ${item.id}`
        }))
      } catch (error) {
        console.error('Error loading products:', error)
        this.productOptions = []
      }
    },
    onActiveLegalEntityChanged() {
      this.emitFilters()
    }
  },
  beforeMount() {
    if (this.showLocalInstitutionFilter) {
      this.loadLegalEntities()
    }
    if (this.showPeopleFilter) {
      this.loadPeople()
    }
    if (this.showProductFilter) {
      this.loadProducts()
    }

    // Aplicar filtro padrão (últimos 7 dias)
    const dates = this.calculateQuickPeriod('last_7_days')
    this.startDate = dates.start
    this.endDate = dates.end
  },
  watch: {
    showPeopleFilter(newVal) {
      if (newVal) {
        this.loadPeople()
      }
    },
    peopleFilterType() {
      if (this.showPeopleFilter) {
        this.loadPeople()
      }
    },
    showLocalInstitutionFilter(newVal) {
      if (newVal) {
        this.loadLegalEntities()
      }
    },
    activeInstitutionId() {
      this.emitFilters()
    },
    showProductFilter(newVal) {
      if (newVal) {
        this.loadProducts()
      }
    },
    value: {
      handler(newVal) {
        if (newVal) {
          this.selectedLegalEntity = newVal.institution_id || null
          this.selectedPeople = newVal.teacher_id || newVal.student_id || null
          this.selectedProduct = newVal.product_id || null
          this.startDate = newVal.start_date || ''
          this.endDate = newVal.end_date || ''
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.$root.$on('activeLegalEntityChanged', this.onActiveLegalEntityChanged)
    this.$nextTick(() => {
      this.emitFilters()
    })
  },
  beforeDestroy() {
    this.$root.$off('activeLegalEntityChanged', this.onActiveLegalEntityChanged)
  }
}
</script>

<style scoped>
.financial-period-filter {
  width: 100%;
}
</style>
